import {
  AlertTriangleIcon,
  ChangeRoleIcon,
  CheckIcon,
} from "@100mslive/react-icons";
import {
  Button,
  Checkbox,
  Dialog,
  Dropdown,
  Flex,
  Loading,
  Text,
} from "@100mslive/react-ui";
import { DialogContent, DialogRow } from "../../primitives/DialogContent";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { DialogDropdownTrigger } from "../../primitives/DropdownTrigger";
import { ROLES_CONVERT } from "../../common/roles";
import { convertRolesName } from "../../common/utils";
import { useFilteredRoles } from "../../common/hooks";
import { useHMSActions } from "@100mslive/react-sdk";
import { useTranslation } from "react-i18next";

export const BulkRoleChangeModal = ({ onOpenChange }) => {
  const { t } = useTranslation();
  const roles = useFilteredRoles();
  const hmsActions = useHMSActions();
  const ref = useRef(null);
  const roleRef = useRef(null);
  const [selectedBulkRole, setBulkRole] = useState([]);
  const [selectedRole, setRole] = useState("");
  const [bulkRoleDialog, setBulkRoleDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmiting, setIsSubmiting] = useState(false);

  const changeBulkRole = useCallback(async () => {
    if (selectedBulkRole.length > 0 && selectedRole) {
      try {
        setIsSubmiting(true);
        await hmsActions.changeRoleOfPeersWithRoles(
          selectedBulkRole,
          selectedRole
        );
        setIsSubmiting(false);
        setErrorMessage("");
        onOpenChange(false);
      } catch (err) {
        setErrorMessage(err?.message ? err?.message : "Unknown error");
        setIsSubmiting(false);
      }
    }
  }, [selectedBulkRole, selectedRole, hmsActions, onOpenChange]);

  const convertSelectedBulkRole = useMemo(() => {
    const data = selectedBulkRole.map(role => {
      return t("obj_roles." + ROLES_CONVERT[convertRolesName(role)]);
    });
    return data;
  }, [selectedBulkRole]);

  return (
    <Dialog.Root defaultOpen onOpenChange={onOpenChange}>
      <DialogContent
        title={t("lbl_change_multiple_role")}
        Icon={ChangeRoleIcon}
      >
        <DialogRow>
          <Text>{t("lbl_select_role")}: </Text>
          <Dropdown.Root
            open={bulkRoleDialog}
            onOpenChange={value => {
              if (value) {
                setBulkRoleDialog(value);
              }
            }}
            modal={false}
          >
            <DialogDropdownTrigger
              ref={ref}
              title={
                selectedBulkRole.length === 0
                  ? t("lbl_select_multiple_role")
                  : convertSelectedBulkRole.toString()
              }
              css={{
                w: "70%",
                p: "$8",
              }}
              open={bulkRoleDialog}
            />
            <Dropdown.Content
              css={{ w: ref.current?.clientWidth, zIndex: 1000 }}
              onInteractOutside={() => {
                if (bulkRoleDialog) {
                  setBulkRoleDialog(false);
                }
              }}
            >
              {roles &&
                roles.map(role => {
                  return (
                    <Dropdown.CheckboxItem
                      key={role}
                      checked={selectedBulkRole.includes(role)}
                      onCheckedChange={value => {
                        setBulkRole(selection => {
                          return value
                            ? [...selection, role]
                            : selection.filter(
                                selectedRole => selectedRole !== role
                              );
                        });
                        setErrorMessage("");
                      }}
                    >
                      <Checkbox.Root
                        css={{ margin: "$2" }}
                        checked={selectedBulkRole.includes(role)}
                      >
                        <Checkbox.Indicator css={{ color: "$surfaceDefault" }}>
                          <CheckIcon width={16} height={16} />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                      {t("obj_roles." + ROLES_CONVERT[convertRolesName(role)])}
                    </Dropdown.CheckboxItem>
                  );
                })}
            </Dropdown.Content>
          </Dropdown.Root>
        </DialogRow>
        <DialogRow>
          <Text>{t("lbl_become_role")} </Text>
          <Dropdown.Root
            open={roleDialog}
            onOpenChange={value => setRoleDialog(value)}
          >
            <DialogDropdownTrigger
              ref={roleRef}
              title={
                ROLES_CONVERT[convertRolesName(selectedRole)]
                  ? t(
                      "obj_roles." +
                        ROLES_CONVERT[convertRolesName(selectedRole)]
                    )
                  : t("lbl_select_role")
              }
              css={{
                w: "70%",
                p: "$8",
              }}
              open={roleDialog}
            />
            <Dropdown.Content
              css={{ w: roleRef.current?.clientWidth, zIndex: 1000 }}
            >
              {roles &&
                roles.map(role => {
                  return (
                    <Dropdown.Item
                      key={role}
                      onSelect={() => {
                        setRole(role);
                        setErrorMessage("");
                      }}
                    >
                      {t("obj_roles." + ROLES_CONVERT[convertRolesName(role)])}
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Content>
          </Dropdown.Root>
        </DialogRow>
        <DialogRow>
          {errorMessage && (
            <Flex gap={2} css={{ c: "$error", w: "70%", ml: "auto" }}>
              <AlertTriangleIcon />
              <Text css={{ c: "inherit" }}>{errorMessage}</Text>
            </Flex>
          )}
        </DialogRow>
        <DialogRow justify="end">
          <Button
            variant="primary"
            onClick={changeBulkRole}
            disabled={!(selectedRole && selectedBulkRole.length > 0)}
          >
            {isSubmiting && <Loading css={{ color: "$textSecondary" }} />}
            {t("lbl_apply")}
          </Button>
        </DialogRow>
      </DialogContent>
    </Dialog.Root>
  );
};
